import { useEffect, useState, useMemo, type ReactNode } from "react";
import { TabNav } from "@/components/tab-nav";
import { Badge } from "@/components/ui/badge";
import { DashboardSection } from "@/components/dashboard-section";
import { PageHeader } from "@/components/page-header";
import { CoolIcon } from "@/components/ui/cool-icon";
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from "@/components/ui/alert";
import {
  Bell,
  AlertTriangle,
  ShieldAlert,
  Newspaper,
  TrendingDown,
  XCircle,
  Loader2,
} from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { motion } from "motion/react";
import { listAlerts, type AlertRow } from "@/lib/api/alerts";

const TABS = [
  { key: "all", label: "All" },
  { key: "breach", label: "Breaches" },
  { key: "drawdown", label: "Drawdown" },
  { key: "news", label: "News Blackout" },
  { key: "compliance", label: "Compliance" },
];

interface Alert {
  id: string;
  type: string;
  severity: "critical" | "warning" | "info";
  title: string;
  detail: string;
  pairId: string | null;
  symbol: string | null;
  createdAt: string;
  read: boolean;
}

/** Wire the loose backend `type` string onto a UI category. The four
 *  buckets match the existing tab list — anything else falls into a
 *  generic group surfaced under "All". */
function bucketType(t: string): string {
  const k = t.toLowerCase();
  if (k.includes("breach")) return "breach";
  if (k.includes("drawdown") || k.includes("dd")) return "drawdown";
  if (k.includes("news") || k.includes("blackout")) return "news";
  if (k.includes("compliance") || k.includes("violation")) return "compliance";
  return t;
}

function toAlert(r: AlertRow): Alert {
  return {
    id: String(r.id),
    type: bucketType(r.type),
    severity: r.severity,
    title: r.title,
    detail: r.detail,
    pairId: r.pair_id,
    symbol: r.symbol,
    createdAt: new Date(r.created_at).toISOString(),
    read: r.read,
  };
}

const severityConfig: Record<
  string,
  {
    icon: typeof AlertTriangle;
    iconClass: string;
    /** Alert variant (shadcn) + extra border tint so severity is readable at a glance. */
    alertVariant: "default" | "destructive";
    rowClass: string;
    badgeVariant: "breached" | "warning" | "passed";
    label: string;
  }
> = {
  critical: {
    icon: XCircle,
    iconClass: "text-destructive",
    alertVariant: "destructive",
    rowClass: "border-destructive/30 bg-destructive/5",
    badgeVariant: "breached",
    label: "Critical",
  },
  warning: {
    icon: AlertTriangle,
    iconClass: "text-amber-500",
    alertVariant: "default",
    rowClass: "border-amber-500/30 bg-amber-500/5",
    badgeVariant: "warning",
    label: "Warning",
  },
  info: {
    icon: Bell,
    iconClass: "text-blue-500",
    alertVariant: "default",
    rowClass: "border-blue-500/20 bg-blue-500/5",
    badgeVariant: "passed",
    label: "Info",
  },
};

const typeIcons: Record<string, typeof AlertTriangle> = {
  breach: ShieldAlert,
  drawdown: TrendingDown,
  news: Newspaper,
  compliance: ShieldAlert,
};

export function AlertsPage(): ReactNode {
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState<Alert[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    listAlerts()
      .then((rs) => setRows(rs.map(toAlert)))
      .catch((e) => {
        setRows([]);
        setLoadError(e instanceof Error ? e.message : String(e));
      });
  }, []);

  const safeRows = rows ?? [];
  const filtered = useMemo(() => {
    if (tab === "all") return safeRows;
    return safeRows.filter((a) => a.type === tab);
  }, [tab, safeRows]);

  const unread = safeRows.filter((a) => !a.read).length;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Alerts"
        subtitle={
          unread > 0
            ? `${unread} Unread · Breach Alerts, Drawdown Warnings, News Blackouts`
            : "Breach Alerts, Drawdown Warnings, News Blackouts"
        }
        icon={<CoolIcon name={unread > 0 ? "Bell_Ring" : "Bell"} />}
        accentWords={["Alerts"]}
      />

      <TabNav tabs={TABS} active={tab} onChange={setTab} />

      {/* Alert list */}
      {rows === null ? (
        <DashboardSection>
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Loader2 className="mb-3 h-5 w-5 animate-spin text-accent" />
            <p className="text-sm text-muted-foreground">Loading alerts…</p>
          </div>
        </DashboardSection>
      ) : loadError ? (
        <DashboardSection>
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-destructive">Couldn't load alerts: {loadError}</p>
          </div>
        </DashboardSection>
      ) : filtered.length === 0 ? (
        <DashboardSection>
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Bell className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {safeRows.length === 0
                ? "No alerts — the system hasn't flagged anything."
                : "No alerts in this category."}
            </p>
          </div>
        </DashboardSection>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((alert, i) => {
            const sev = severityConfig[alert.severity]!;
            const TypeIcon = typeIcons[alert.type] ?? Bell;
            const SevIcon = sev.icon;

            return (
              <motion.div
                key={alert.id}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
              >
                <Alert
                  variant={sev.alertVariant}
                  className={cn(
                    "p-4",
                    sev.rowClass,
                    !alert.read && "border-l-2 border-l-accent",
                  )}
                >
                  <SevIcon className={cn("size-4", sev.iconClass)} />
                  <AlertTitle className="flex flex-wrap items-center gap-2">
                    <span>{alert.title}</span>
                    {alert.symbol && (
                      <Badge variant="outline" size="xs">
                        {alert.symbol}
                      </Badge>
                    )}
                    <Badge variant={sev.badgeVariant} size="xs">
                      <SevIcon className="size-2.5" />
                      {sev.label}
                    </Badge>
                  </AlertTitle>
                  <AlertDescription>
                    <p className="text-[11px] leading-relaxed">{alert.detail}</p>
                    <div className="mt-2 flex items-center gap-2 text-[10px] text-muted-foreground/60">
                      <TypeIcon className="h-3 w-3" />
                      <span className="capitalize">{alert.type}</span>
                      <span>&middot;</span>
                      <span className="font-mono">{alert.createdAt.replace("T", " ")}</span>
                      {alert.pairId && (
                        <>
                          <span>&middot;</span>
                          <span className="font-mono">{alert.pairId}</span>
                        </>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
